/**
 * Lista de conversas: scroll infinito, contadores, polling e sync realtime.
 */
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { ChatConversation } from './types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useConversationInfiniteScroll } from './useConversationInfiniteScroll';
import { useConversationCounters } from './useConversationCounters';
import { useChatPolling } from './useChatPolling';
import { useChatRealtimeSync } from './useChatRealtimeSync';
import { useAppContext } from '../../../contexts/AppContext';
import { jidsMatch } from '../../../utils/phoneUtils';
import { isInstagramCdnAvatarUrl } from '../../../utils/failedImageCache';
import { refreshInstagramContactAvatars } from '../../../services/modules/instagramRepository';

const instagramAvatarRefreshStarted = new Set<string>();

type MetaDetail = {
    phone?: string;
    userId?: string | null;
    status?: string;
    unreadCount?: number;
    remoteJid?: string;
    connectionId?: string;
};

function conversationMatchesMeta(
    c: ChatConversation,
    phone?: string,
    remoteJid?: string,
    connectionId?: string,
): boolean {
    // Mesmo telefone em 2 conexões: só atualiza a conexão do evento
    if (connectionId && c.connectionId && c.connectionId !== connectionId) return false;

    if (remoteJid && (c.id === remoteJid || jidsMatch(c.id, remoteJid))) return true;
    // Instagram só casa por remote_jid — dígitos de IGSID não são telefone
    if (c.id.startsWith('ig:') || c.platform === 'instagram') return false;
    if (phone) {
        const digits = phone.replace(/\D/g, '');
        // Evita includes() curto que marca conversas erradas
        if (digits.length < 8) return false;
        return jidsMatch(c.id, phone) || jidsMatch(c.id, digits);
    }
    return false;
}

export function useChatConversationList(connectionId: string | null) {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { currentPage } = useAppContext();
    const isChatActive = currentPage === 'chat';
    const needsConversations = isChatActive || currentPage === 'dashboard';
    const userId = user?.id || null;
    const orgId = currentOrganization?.id;

    const {
        conversations,
        setConversations,
        isLoading: isLoadingConversations,
        isLoadingMore,
        hasMoreConversations,
        loadInitial,
        refresh,
        loadMore,
    } = useConversationInfiniteScroll({
        organizationId: orgId,
        connectionId: connectionId || undefined,
        enabled: needsConversations && !!currentOrganization,
    });

    const {
        counters: backendCounters,
        refreshCounters,
    } = useConversationCounters({
        organizationId: orgId,
        userId: userId || undefined,
        connectionId: connectionId || undefined,
        enabled: isChatActive && !!currentOrganization,
    });

    const loadInitialRef = useRef(loadInitial);
    loadInitialRef.current = loadInitial;

    useEffect(() => {
        if (needsConversations && orgId) loadInitialRef.current();
    }, [needsConversations, orgId, connectionId]);

    const refreshConversations = useCallback(async (): Promise<boolean> => {
        if (!orgId) return false;
        const [listChanged] = await Promise.all([refresh(), refreshCounters()]);
        return listChanged;
    }, [orgId, refresh, refreshCounters]);

    useChatPolling(refreshConversations, 15000, 120000, isChatActive && !!currentOrganization);
    useChatRealtimeSync(refreshConversations, isChatActive);

    // Primitivo estável: evita reexecutar o effect a cada refresh da lista (React useEffect + array dep)
    const needsInstagramCdnAvatarRefresh = useMemo(
        () => conversations.some(
            (conversation) => conversation.platform === 'instagram' && isInstagramCdnAvatarUrl(conversation.avatar),
        ),
        [conversations],
    );

    useEffect(() => {
        if (!isChatActive || !orgId || !needsInstagramCdnAvatarRefresh) return;
        if (instagramAvatarRefreshStarted.has(orgId)) return;
        instagramAvatarRefreshStarted.add(orgId);
        void refreshInstagramContactAvatars(orgId).then((updated) => {
            if (updated && updated > 0) window.dispatchEvent(new CustomEvent('avatar:synced'));
        });
    }, [isChatActive, orgId, needsInstagramCdnAvatarRefresh]);

    useEffect(() => {
        if (!isChatActive) return;

        const handleAssignmentChange = (e: Event) => {
            const detail = (e as CustomEvent<MetaDetail>).detail;
            const phone = detail?.phone;
            const remoteJid = detail?.remoteJid;
            const connectionId = detail?.connectionId;
            const hasIdentity = Boolean(phone || remoteJid);
            const hasMeta = detail?.status !== undefined || detail?.unreadCount !== undefined;

            if (hasIdentity || hasMeta) {
                setConversations((prev) => {
                    let matched = false;
                    const next = prev.map((c) => {
                        const hit = conversationMatchesMeta(c, phone, remoteJid, connectionId);
                        // Sem identity: não aplica patch por status/unread em massa (comportamento atual)
                        if (!hit) return c;
                        matched = true;
                        return {
                            ...c,
                            ...(Object.prototype.hasOwnProperty.call(detail, 'userId')
                                ? { assignedTo: detail.userId || undefined }
                                : {}),
                            ...(detail.status !== undefined
                                ? { status: detail.status === 'closed' ? 'closed' as const : undefined }
                                : {}),
                            ...(detail.unreadCount !== undefined ? { unreadCount: detail.unreadCount } : {}),
                        };
                    });
                    if (!matched && hasIdentity) queueMicrotask(() => { void refresh(); });
                    return next;
                });
            } else {
                void refresh();
            }
            refreshCounters();
        };

        const handleAvatarSynced = () => { refresh(); };

        window.addEventListener('assignment:changed', handleAssignmentChange);
        window.addEventListener('conversation:meta_changed', handleAssignmentChange);
        window.addEventListener('avatar:synced', handleAvatarSynced);
        return () => {
            window.removeEventListener('assignment:changed', handleAssignmentChange);
            window.removeEventListener('conversation:meta_changed', handleAssignmentChange);
            window.removeEventListener('avatar:synced', handleAvatarSynced);
        };
    }, [isChatActive, refresh, refreshCounters, setConversations]);

    return {
        conversations,
        setConversations,
        isLoadingConversations,
        connectionId,
        userId,
        isLoadingMore,
        hasMoreConversations,
        loadMoreConversations: loadMore,
        backendCounters,
    };
}
